<template>
  <div>
    <div class="page-header">
      <div>
        <h5 class="page-title">Pre-Register a Visitor</h5>
        <p class="page-sub">Schedule a visit in advance and send the visitor a QR invite</p>
      </div>
    </div>

    <div class="form-card" style="max-width:680px;margin:0 auto;">

      <!-- Visitor Details -->
      <div class="section-label">Visitor Details</div>
      <div class="form-cols">
        <div class="form-row">
          <label class="form-label">First Name <span class="req">*</span></label>
          <input v-model="form.first_name" class="form-input" required />
        </div>
        <div class="form-row">
          <label class="form-label">Last Name <span class="req">*</span></label>
          <input v-model="form.last_name" class="form-input" required />
        </div>
      </div>
      <div class="form-cols" style="margin-top:12px;">
        <div class="form-row">
          <label class="form-label">Email</label>
          <input v-model="form.email" type="email" class="form-input" />
        </div>
        <div class="form-row">
          <label class="form-label">Phone</label>
          <input v-model="form.phone" class="form-input" />
        </div>
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Company / Organisation</label>
        <input v-model="form.company" class="form-input" />
      </div>

      <hr class="section-divider" />

      <!-- Visit Details -->
      <div class="section-label">Visit Details</div>
      <div class="form-cols">
        <div class="form-row">
          <label class="form-label">Host (Who are they visiting?) <span class="req">*</span></label>
          <!-- ESS mode: locked to logged-in employee -->
          <div v-if="isEss" class="locked-host">
            <span v-if="loadingEmployee" class="locked-host-name text-muted">Loading…</span>
            <span v-else class="locked-host-name">{{ lockedHostName || '—' }}</span>
            <span class="locked-host-badge">You</span>
          </div>
          <!-- HR mode: employee dropdown or free text -->
          <template v-else>
            <div class="host-mode-toggle" style="margin-bottom:6px;">
              <button type="button"
                :class="['host-mode-btn', hostMode === 'employee' ? 'host-mode-btn--active' : '']"
                @click="hostMode = 'employee'; form.host_name = ''">
                Employee
              </button>
              <button type="button"
                :class="['host-mode-btn', hostMode === 'text' ? 'host-mode-btn--active' : '']"
                @click="hostMode = 'text'; form.host_employee_id = ''">
                Free text
              </button>
            </div>
            <search-select
              v-if="hostMode === 'employee'"
              v-model="form.host_employee_id"
              url="payroll/employees/active"
              query-param="search"
              label-key="full_name"
              sublabel-key="employee_no"
              value-key="id"
              response-key="data"
              placeholder="— Select host —"
              search-placeholder="Type name or number…"
              :min-chars="1"
            />
            <input
              v-else
              v-model="form.host_name"
              class="form-input"
              placeholder="e.g. Finance Department, Room 3B…"
            />
          </template>
        </div>
        <div class="form-row">
          <label class="form-label">Expected Date &amp; Time <span class="req">*</span></label>
          <input v-model="form.scheduled_at" type="datetime-local" class="form-input" required />
        </div>
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Visitor Type</label>
        <select v-model="form.visitor_type_id" class="form-input">
          <option value="">— Select type (optional) —</option>
          <option v-for="t in visitorTypes" :key="t.id" :value="t.id">{{ t.name }}</option>
        </select>
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Purpose of Visit <span class="req">*</span></label>
        <input v-model="form.purpose" class="form-input" required maxlength="300" />
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Location / Floor / Room</label>
        <input v-model="form.location" class="form-input" />
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Items to be Deposited</label>
        <input v-model="form.items_deposited" class="form-input" placeholder="e.g. Laptop, ID card, Mobile phone…" maxlength="300" />
        <span class="form-hint">List any items the visitor will leave at reception</span>
      </div>
      <div class="form-row" style="margin-top:12px;">
        <label class="form-label">Additional Notes</label>
        <textarea v-model="form.note" class="form-input" rows="3" style="resize:vertical;min-height:80px;line-height:1.5;"></textarea>
      </div>

      <div style="display:flex;justify-content:flex-end;margin-top:20px;">
        <button class="btn-primary-action" @click="submit" :disabled="saving">
          <font-awesome-icon v-if="saving" icon="spinner" spin />
          {{ saving ? 'Saving…' : 'Register Visitor' }}
        </button>
      </div>
    </div>

    <!-- QR Modal -->
    <div v-if="qrResult" class="modal-overlay" @click.self="qrResult = null">
      <div class="modal-box" style="max-width:420px;">
        <div class="modal-head">
          <h5 class="modal-title">Visitor Pre-Registered!</h5>
          <button class="modal-close" @click="qrResult = null"><font-awesome-icon icon="times" /></button>
        </div>
        <div class="modal-body" style="align-items:center;text-align:center;">
          <p class="text-muted" style="margin:0;">Share the QR code or the self-check-in link below with your visitor so they can get their pass instantly on arrival.</p>
          <img :src="qrResult.qr_url" alt="QR Code" style="width:180px;height:180px;" />
          <div class="detail-grid" style="width:100%;">
            <div class="detail-row">
              <span class="detail-label">Visitor</span>
              <span class="detail-value">{{ qrResult.visit.visitor?.first_name }} {{ qrResult.visit.visitor?.last_name }}</span>
            </div>
            <div class="detail-row">
              <span class="detail-label">Scheduled</span>
              <span class="detail-value">{{ fmtDatetime(qrResult.visit.scheduled_at) }}</span>
            </div>
          </div>
          <div class="prereg-link-box" style="width:100%;">
            <div class="prereg-link-label">Self-check-in link for visitor</div>
            <div class="prereg-link-row">
              <input readonly :value="selfCheckInLink" class="prereg-link-input" @click="$event.target.select()" />
              <button class="btn-ghost btn-sm" @click="copyLink" style="flex-shrink:0;">
                <font-awesome-icon :icon="linkCopied ? 'check' : 'copy'" />
                {{ linkCopied ? 'Copied!' : 'Copy' }}
              </button>
            </div>
            <p class="prereg-link-hint">Visitor can use this link to self-check-in on arrival</p>
          </div>
        </div>
        <div class="modal-footer">
          <button class="btn-ghost" @click="qrResult = null">Close</button>
          <router-link
            :to="isEss ? $url('/hr/visitors/upcoming') : $url('/hr/visitors/history')"
            class="btn-primary-action"
          >{{ isEss ? 'View Upcoming' : 'Visit History' }}</router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { useToast } from 'vue-toastification'
import SearchSelect from '../../../../components/SearchSelect.vue'

export default {
  name: 'VisitorPreRegister',
  components: { SearchSelect },
  setup() { return { toast: useToast() } },
  data() {
    return {
      visitorTypes: [],
      form: {
        first_name: '', last_name: '', email: '', phone: '',
        company: '', host_employee_id: '', host_name: '', purpose: '',
        scheduled_at: '', location: '', items_deposited: '', note: '',
        visitor_type_id: '',
      },
      hostMode:        'employee',
      saving:          false,
      qrResult:        null,
      linkCopied:      false,
      lockedHostName:  '',
      loadingEmployee: false,
    }
  },
  computed: {
    isEss() {
      return !!this.$route.query.ess
    },
    selfCheckInLink() {
      if (!this.qrResult) return ''
      const cc  = this.$route.params.companyCode
      const bid = this.qrResult.visit?.business_id
      const tok = this.qrResult.qr_token
      if (!cc || !bid || !tok) return ''
      return `${window.location.origin}/visitor-checkin/${cc}/${bid}?token=${tok}`
    },
  },
  async created() {
    try {
      const r = await axios.get('visitors/types')
      this.visitorTypes = (r.data.data ?? r.data).filter(t => t.active)
    } catch { /* silent — types are optional */ }
    if (this.isEss) {
      this.loadingEmployee = true
      try {
        const r = await axios.get('visitors/me/employee-info')
        const emp = r.data.data
        this.form.host_employee_id = emp.id
        this.lockedHostName = emp.full_name + (emp.employee_no ? ` (${emp.employee_no})` : '')
      } catch {
        this.toast.error('Could not load your employee profile.')
      } finally {
        this.loadingEmployee = false
      }
    }
  },
  methods: {
    async submit() {
      this.saving = true
      try {
        const endpoint = this.isEss ? 'visitors/me' : 'visitors/pre-register'
        const r = await axios.post(endpoint, this.form)
        this.qrResult = r.data.data
        this.form = { first_name: '', last_name: '', email: '', phone: '', company: '',
          host_employee_id: this.isEss ? this.form.host_employee_id : '',
          host_name: '', purpose: '', scheduled_at: '', location: '', note: '' }
        if (!this.isEss) this.hostMode = 'employee'
        this.toast.success('Visitor pre-registered!')
      } catch (e) {
        this.toast.error(e.response?.data?.message ?? 'Failed to register visitor.')
      } finally {
        this.saving = false
      }
    },
    fmtDatetime(v) {
      if (!v) return '—'
      return new Date(v).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' })
    },
    copyLink() {
      navigator.clipboard.writeText(this.selfCheckInLink)
        .then(() => {
          this.linkCopied = true
          setTimeout(() => { this.linkCopied = false }, 2000)
        })
        .catch(() => this.toast.error('Could not copy link.'))
    },
  },
}
</script>

<style scoped>
.page-header { display:flex; align-items:flex-start; justify-content:space-between; flex-wrap:wrap; gap:12px; margin-bottom:20px; }
.page-title  { font-size:20px; font-weight:700; color:#1a2b45; margin:0 0 3px; }
.page-sub    { font-size:13px; color:#7a90b0; margin:0; }

.btn-primary-action { display:inline-flex; align-items:center; gap:6px; background:#004AAD; color:#fff; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; border:none; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-primary-action:hover:not(:disabled) { background:#003b8e; }
.btn-primary-action:disabled { opacity:.6; cursor:not-allowed; }
.btn-ghost { display:inline-flex; align-items:center; gap:6px; background:#f4f7fb; color:#3d5068; border:1px solid #e4eaf2; font-size:13px; font-weight:600; padding:0 18px; height:38px; border-radius:8px; cursor:pointer; font-family:inherit; text-decoration:none; }
.btn-ghost:hover { background:#e4eaf2; }

.form-card  { background:#fff; border:1px solid #e4eaf2; border-radius:14px; padding:24px; }
.form-cols  { display:grid; grid-template-columns:1fr 1fr; gap:14px; }
.form-row   { display:flex; flex-direction:column; gap:5px; }
.form-label { font-size:12px; font-weight:600; color:#4a5e78; }
.form-input { width:100%; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; font-size:13px; color:#1a2b45; outline:none; box-sizing:border-box; font-family:inherit; }
.form-input:focus { border-color:#004AAD; }
.req { color:#dc2626; }

.section-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:14px; }
.section-divider { border:none; border-top:1px solid #f0f4f8; margin:20px 0; }

.locked-host { display:flex; align-items:center; gap:8px; padding:9px 12px; border:1px solid #dde5f0; border-radius:8px; background:#f8fafc; min-height:38px; }
.locked-host-name { font-size:13px; color:#1a2b45; font-weight:600; flex:1; }
.locked-host-badge { font-size:10px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; background:#e8f0fe; color:#004AAD; border-radius:4px; padding:2px 7px; }

.modal-overlay { position:fixed; inset:0; background:rgba(0,0,0,.45); z-index:1000; display:flex; align-items:center; justify-content:center; padding:16px; }
.modal-box   { background:#fff; border-radius:14px; width:100%; max-width:520px; max-height:92vh; overflow-y:auto; box-shadow:0 20px 60px rgba(0,0,0,.2); }
.modal-head  { display:flex; align-items:center; justify-content:space-between; padding:18px 22px; border-bottom:1px solid #f0f4f8; }
.modal-title { font-size:15px; font-weight:700; color:#1a2b45; margin:0; }
.modal-close { background:none; border:none; cursor:pointer; color:#7a90b0; font-size:16px; line-height:1; }
.modal-body  { padding:20px 22px; display:flex; flex-direction:column; gap:14px; }
.modal-footer { display:flex; justify-content:flex-end; gap:8px; padding:16px 22px; border-top:1px solid #f0f4f8; }

.detail-grid { background:#f8fafc; border-radius:8px; padding:12px 14px; }
.detail-row  { display:flex; justify-content:space-between; align-items:center; padding:6px 0; border-bottom:1px solid #f0f4f8; font-size:13px; }
.detail-row:last-child { border-bottom:none; }
.detail-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#aab8cc; }
.detail-value { font-weight:600; color:#1a2b45; font-size:13px; }
.text-muted { color:#7a90b0; font-size:13px; }

.prereg-link-box { background:#f8fafc; border:1px solid #e4eaf2; border-radius:10px; padding:14px; text-align:left; }
.prereg-link-label { font-size:11px; font-weight:700; text-transform:uppercase; letter-spacing:.04em; color:#7a90b0; margin-bottom:8px; }
.prereg-link-row { display:flex; gap:8px; align-items:center; }
.prereg-link-input { flex:1; min-width:0; padding:8px 10px; border:1px solid #dde5f0; border-radius:8px; font-size:12px; color:#3d5068; background:#fff; font-family:monospace; outline:none; cursor:text; }
.prereg-link-hint { font-size:11px; color:#aab8cc; margin-top:6px; }

.host-mode-toggle { display:inline-flex; border:1px solid #dde5f0; border-radius:6px; overflow:hidden; }
.host-mode-btn { padding:3px 10px; background:#f8fafc; border:none; cursor:pointer; font-size:11px; font-weight:600; color:#7a90b0; font-family:inherit; }
.host-mode-btn--active { background:#004AAD; color:#fff; }
.host-mode-btn:not(.host-mode-btn--active):hover { background:#f0f4f8; }

@media (max-width:600px) { .form-cols { grid-template-columns:1fr; } }
</style>
